import { useState, useCallback } from 'react';
import log from '../utilities/log';
import mediator from '../utilities/mediator';
import i18n from '../utilities/i18n';
import { DOWNLOAD_CONFIG, DOWNLOAD_MODE, DownloadStatus } from '../constants';

export interface DownloadParams {
  downloadMode: (typeof DOWNLOAD_MODE)[keyof typeof DOWNLOAD_MODE];
  studyKey: string;
  studyName: string;
  subjectKeys?: string[];
}

export interface DownloadState {
  status: DownloadStatus;
  progress?: number;
  downloadUrl?: string;
  filename?: string;
  checksumDownloadUrl?: string;
  checksumFilename?: string;
  errorMessage?: string;
}

type DownloadPackageResult = {
  blob: Blob;
  checksumSha256?: string;
};

/**
 * Hook for exporting study data with progress tracking and download handling
 */
export function useStudyDownload() {
  const [downloadState, setDownloadState] = useState<DownloadState>({
    status: DownloadStatus.IDLE,
  });

  const downloadStudyData = useCallback(
    async (params: DownloadParams) => {
      // Prevent concurrent downloads
      if (
        downloadState.status === DownloadStatus.PREPARING ||
        downloadState.status === DownloadStatus.PROCESSING
      ) {
        const error = new Error(
          i18n.t('Modal_Download.error_already_in_progress'),
        );

        log.warn('Download already in progress, rejecting new request');
        throw error;
      }

      try {
        // Set initial status
        setDownloadState({
          status: DownloadStatus.PREPARING,
          progress: 0,
        });

        // Set processing status
        setDownloadState((prev) => ({
          ...prev,
          status: DownloadStatus.PROCESSING,
        }));

        // Build the mediator request
        let topic: string;
        let requestData: Record<string, any>;

        switch (params.downloadMode) {
          case DOWNLOAD_MODE.STUDY:
            topic = 'study:downloadStudyData';
            requestData = { studyKey: params.studyKey };
            break;
          case DOWNLOAD_MODE.SUBJECTS:
            topic = 'study:downloadSelectedSubjectData';
            requestData = { subjectKeys: params.subjectKeys };
            break;
          default:
            throw new Error(`Invalid download mode: ${params.downloadMode}`);
        }

        const mediatorRequest = {
          ...requestData,
          onProgress: (progressPercent: number) => {
            setDownloadState((prev) => ({
              ...prev,
              progress: progressPercent,
            }));
          },
        };

        // Use the service for actual download
        const downloadResult = await mediator.request<DownloadPackageResult>(
          topic,
          mediatorRequest,
        );

        // Create download URL and update state
        const { blob, checksumSha256 } = downloadResult;
        const url = window.URL.createObjectURL(blob);
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        const filename = `${params.studyName}_${timestamp}${DOWNLOAD_CONFIG.FILE_EXTENSION}`;
        let checksumDownloadUrl: string | undefined;
        let checksumFilename: string | undefined;

        if (checksumSha256) {
          const checksumText = `${checksumSha256}  ${filename}\n`;
          const checksumBlob = new Blob([checksumText], {
            type: 'text/plain;charset=utf-8',
          });

          checksumDownloadUrl = window.URL.createObjectURL(checksumBlob);
          checksumFilename = `${filename}.sha256`;
        }

        // Clean up download URLs and update state
        setDownloadState((prev) => {
          if (prev.downloadUrl && prev.downloadUrl !== url) {
            window.URL.revokeObjectURL(prev.downloadUrl);
          }

          if (
            prev.checksumDownloadUrl &&
            prev.checksumDownloadUrl !== checksumDownloadUrl
          ) {
            window.URL.revokeObjectURL(prev.checksumDownloadUrl);
          }

          return {
            ...prev,
            status: DownloadStatus.COMPLETED,
            downloadUrl: url,
            filename,
            checksumDownloadUrl,
            checksumFilename,
            errorMessage: undefined,
          };
        });

        return {
          success: true,
          filename,
          downloadUrl: url,
          checksumDownloadUrl,
          checksumFilename,
        };
      } catch (error) {
        log.error('download failed:', error);

        setDownloadState((prev) => ({
          ...prev,
          status: DownloadStatus.FAILED,
          errorMessage:
            error instanceof Error
              ? error.message
              : i18n.t('Modal_Download.error_generic_failed'),
        }));
        throw error;
      }
    },
    [downloadState.status],
  );

  // Clean up download URLs and reset state
  const resetDownload = useCallback(() => {
    if (downloadState.downloadUrl) {
      window.URL.revokeObjectURL(downloadState.downloadUrl);
    }

    if (downloadState.checksumDownloadUrl) {
      window.URL.revokeObjectURL(downloadState.checksumDownloadUrl);
    }

    setDownloadState({
      status: DownloadStatus.IDLE,
    });
  }, [downloadState.downloadUrl, downloadState.checksumDownloadUrl]);

  return {
    downloadStudyData,
    resetDownload,
    isPending:
      downloadState.status === DownloadStatus.PREPARING ||
      downloadState.status === DownloadStatus.PROCESSING,
    downloadState,
  };
}

export default useStudyDownload;
